import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Navbar from '../components/layout/Navbar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t('auth.verification.noToken') || 'No verification token provided');
        return;
      }

      try {
        await axios.post(`${API_URL}/api/auth/verify-email`, { token });
        setStatus('success');
        setMessage(t('auth.verification.success') || 'Your email has been verified successfully!');
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || t('auth.verification.error') || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token, t]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-4" />
                <CardTitle>{t('auth.verification.verifying') || 'Verifying your email...'}</CardTitle>
                <CardDescription>{t('auth.verification.pleaseWait') || 'Please wait a moment'}</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <CardTitle className="text-green-600">{t('auth.verification.successTitle') || 'Email Verified!'}</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <CardTitle className="text-red-600">{t('auth.verification.errorTitle') || 'Verification Failed'}</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="text-center">
            {status === 'success' && (
              <Button onClick={() => navigate('/login')} className="w-full">
                {t('auth.verification.goToLogin') || 'Go to Login'}
              </Button>
            )}
            {status === 'error' && (
              <div className="space-y-3">
                <Button variant="outline" onClick={() => navigate('/register')} className="w-full">
                  {t('auth.verification.tryAgain') || 'Register Again'}
                </Button>
                <Link to="/" className="block text-sm text-muted-foreground hover:text-primary">
                  {t('common.backToHome') || 'Back to Home'}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
